import HttpClient, { IResponseModel } from "../HttpClient";

import type { IOpenVesselDefinitionV1 } from "open-vessel-definition";

/** POST /ovdConverter — the api-tvd-lambdas `converterHandler`. Access is gated
 *  per-organization by `ovdNsdConverter`, so a 400 "Unsupported action" means the
 *  org is not allowed that direction, not that the payload was wrong. */
const CONVERTER_URL = "ovdConverter";

const OvdNsdConverterService = {
  /**
   * Convert a .nsd file into an OpenVesselDefinition.
   *
   * The File is sent base64-encoded — the API forwards that string to the
   * converter untouched, so the bytes are never re-encoded on the way.
   *
   * The filename travels with it and is NOT cosmetic: vessel identity (IMO,
   * operator, class, name) is read from it, never from the names embedded in the
   * .nsd, which go stale on sister and renamed hulls.
   */
  nsdToOvd: async (
    file: File,
    options: INsdToOvdOptions = {},
  ): Promise<IResponseModel<INsdToOvdResult>> => {
    const nsd = await readFileAsBase64(file);
    const client = new HttpClient();

    return await client.request<INsdToOvdResult>({
      method: "POST",
      url: CONVERTER_URL,
      data: { op: "nsd2ovd", filename: file.name, nsd, ...options },
    });
  },

  /** Build a .nsd from an OpenVesselDefinition. The .nsd comes back base64-encoded. */
  ovdToNsd: async (
    ovd: IOpenVesselDefinitionV1,
    options: IOvdToNsdOptions = {},
  ): Promise<IResponseModel<IOvdToNsdResult>> => {
    const client = new HttpClient();

    return await client.request<IOvdToNsdResult>({
      method: "POST",
      url: CONVERTER_URL,
      data: { op: "ovd2nsd", ovd, ...options },
    });
  },
};

/** Read a File as plain base64. FileReader hands back a data URL, so the
 *  `data:...;base64,` prefix is stripped — the API expects the payload alone. */
export function readFileAsBase64(file: File): Promise<string> {
  const reader = new FileReader();

  return new Promise((resolve, reject) => {
    reader.onload = () => {
      const result = reader.result;

      if (typeof result !== "string") {
        reject({ message: "File Read error" });
        return;
      }

      resolve(result.slice(result.indexOf(",") + 1));
    };

    reader.onerror = () => reject(reader.error);

    reader.readAsDataURL(file);
  });
}

/** Turn the base64 .nsd from `ovdToNsd` back into binary, for download or upload. */
export function nsdBase64ToBlob(nsdBase64: string): Blob {
  const binary = atob(nsdBase64);
  const bytes = new Uint8Array(binary.length);

  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);

  return new Blob([bytes], { type: "application/octet-stream" });
}

export default OvdNsdConverterService;

export interface INsdToOvdOptions {
  /** metres; midship-referenced files need it to shift CGs to aft */
  lbp?: number;
  /** not stored in the .nsd; sets shipData.imoCode */
  imo?: string;
  /** metres */
  loa?: number;
  /** Overrides the VCG→bottomBase ratio. Omitted → the file's own stored ratio
   *  (a stored 0 is 0). Pass 0 to emit the file's VCGs as bottomBases verbatim,
   *  e.g. when the user declines a by-Stack file's stored ratio (nsd-check
   *  `confirmHeightFactor`) — 0 is a VALUE, so send it through (`hf ?? undefined`). */
  heightFactor?: number;
}

export interface IOvdToNsdOptions {
  /** "stack" (default): VCGs by stack with ratio 0, each Bottom VCG = bottomBase.
   *  "tier": the per-tier ladder, converted with `heightFactor`. */
  vcgMode?: "stack" | "tier";
  /** A trailing 40' pair whose fore half accepts nothing: "move" (default) puts
   *  the 40s in the fore bay and still declares the 40' partner, as Ship Editor's
   *  own files do; "collapse" also stops declaring it, so the vessel ends at the
   *  fore bay; "keep" leaves the pair as the OVD has it. */
  trailing40s?: "move" | "collapse" | "keep";
  /** "tier" only (ignored for "stack"). Omitted → the OVD's own
   *  vcgOptions.heightFactor, else 0.45. 0 is a VALUE, so send it through
   *  (`hf ?? undefined`, never `hf || undefined`). The factor used is recorded
   *  in the generated file. */
  heightFactor?: number;
  /** 4-char format token; defaults to the OVD's shipClass */
  token?: string;
}

export interface INsdToOvdResult {
  op: "nsd2ovd";
  ovd: IOpenVesselDefinitionV1;
}

export interface IOvdToNsdResult {
  op: "ovd2nsd";
  /** base64-encoded .nsd bytes — use `nsdBase64ToBlob` to get a downloadable file */
  nsd: string;
  bytes: number;
  /** Notes about what could not be reproduced exactly. NOT errors: a successful
   *  conversion normally returns some. Worth showing to the user. */
  residuals: string[];
}
